import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Space, 
  Typography, 
  Tag, 
  message, 
  Tooltip, 
  Divider,
  Row,
  Col,
  Modal,
  Select
} from 'antd';
import { 
  FileExcelOutlined, 
  FilterOutlined, 
  ClockCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import { useTranslation } from 'react-i18next';
import DeliveryOrderModalB2 from './DeliveryOrderModalB2';
import moment from 'moment';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const DeliveryReport = () => {
  const { t } = useTranslation();
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState([
  dayjs().startOf("month"), // 1st of this month
  dayjs().endOf("month")    // last day of this month
]);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [updating, setUpdating] = useState({});
  
  const API_URL = process.env.REACT_APP_API_URL;

  // Format date and time
  const formatDateTime = (dateString) => {
    return moment(dateString).format('YYYY-MM-DD HH:mm:ss');
  };

  // Fetch delivery report data
  useEffect(() => {
    const fetchDeliveryData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/inventory-b2/delivery-report`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        const sortedData = response.data
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .map(item => ({
            ...item,
            key: `${item.log_id}`,
            formattedDate: formatDateTime(item.timestamp),
            studentName: item.firstname && item.lastname 
              ? `${item.firstname} ${item.lastname}${item.nickname ? ` (${item.nickname})` : ''}`
              : item.student_id || 'System Adjust'
          }));
          
        setReportData(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching delivery report:', err);
        setError('Failed to fetch delivery report.');
        message.error('Failed to fetch delivery report');
        setLoading(false);
      }
    };

    fetchDeliveryData();
  }, [API_URL]);

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // Apply filters
  const applyFilters = () => {
    let filtered = reportData;

    // Apply date range filter
    if (dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].startOf('day').valueOf();
      const endDate = dateRange[1].endOf('day').valueOf();
      
      filtered = filtered.filter(item => {
        const timestamp = new Date(item.timestamp).getTime();
        return timestamp >= startDate && timestamp <= endDate;
      });
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(item => item.delivery_status === statusFilter);
    }

    setFilteredData(filtered);
  };

  // Reset filters
  const resetFilters = () => {
    setDateRange([null, null]);
    setStatusFilter('');
    setFilteredData(reportData);
  };

  // Refresh data
  const refreshData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/inventory-b2/delivery-report`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const sortedData = response.data
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .map(item => ({
          ...item,
          key: `${item.log_id}`,
          formattedDate: formatDateTime(item.timestamp),
          studentName: item.firstname && item.lastname 
            ? `${item.firstname} ${item.lastname}${item.nickname ? ` (${item.nickname})` : ''}`
            : item.student_id || 'Unknown Student'
        }));
        
      setReportData(sortedData);
      setFilteredData(sortedData);
      message.success('Delivery data refreshed');
    } catch (err) {
      console.error('Error refreshing delivery report:', err);
      message.error('Failed to refresh delivery report');
    } finally {
      setLoading(false);
    }
  };

const updateDeliveryStatus = async (logId, status) => {
  setUpdating(prev => ({ ...prev, [logId]: true }));
  
  try {
    const token = localStorage.getItem('token');

    await axios.post(
      `${API_URL}/inventory-b2/inventorylog/${logId}/delivery-status-post`,
      { status },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    // Update local state
    const updatedData = reportData.map(item => {
      if (item.log_id === logId) {
        return {
          ...item,
          delivery_status: status,
          deliver_date: status === 'DELIVERED' ? new Date().toISOString() : null
        };
      }
      return item;
    });
    
    setReportData(updatedData);
    setFilteredData(updatedData);
    
    message.success(`Delivery ${status.toLowerCase()} successfully`);
  } catch (err) {
    console.error('❌ Error updating delivery status (POST):', err);
    message.error(err.response?.data?.error || 'Failed to update delivery status');
  } finally {
    setUpdating(prev => ({ ...prev, [logId]: false }));
  }
};

const resetDeliveryStatus = async (logId) => {
  setUpdating(prev => ({ ...prev, [logId]: true }));
  
  try {
    const token = localStorage.getItem('token');

    await axios.put(
      `${API_URL}/inventory-b2/inventorylog/${logId}/delivery-status-reset`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    // Update local state
    const updatedData = reportData.map(item => {
      if (item.log_id === logId) {
        return {
          ...item,
          delivery_status: 'PENDING',
          deliver_date: null,
          admin_nickname: null
        };
      }
      return item;
    });
    
    setReportData(updatedData);
    setFilteredData(updatedData);
    
    message.success('Delivery status reset to pending successfully');
  } catch (err) {
    console.error('❌ Error resetting delivery status:', err);
    message.error(err.response?.data?.error || 'Failed to reset delivery status');
  } finally {
    setUpdating(prev => ({ ...prev, [logId]: false }));
  }
};

const showResetConfirm = (logId, studentName, currentStatus) => {
  confirm({
    title: 'Reset to Pending?',
    icon: <ExclamationCircleOutlined />,
    content: `Are you sure you want to reset delivery status for ${studentName} back to PENDING? ${currentStatus === 'DELIVERED' ? 'This will reverse the stock adjustment.' : ''}`,
    okText: 'Yes, Reset',
    okType: 'danger',
    cancelText: 'No',
    onOk() {
      resetDeliveryStatus(logId);
    },
  });
};
  // Show confirmation modal
  const showConfirm = (logId, status, studentName) => {
    const isDelivered = status === 'DELIVERED';
    confirm({
      title: `Confirm ${status.toLowerCase()}?`,
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to mark this delivery to ${studentName} as ${status.toLowerCase()}?`,
      okText: 'Yes',
      okType: isDelivered ? 'primary' : 'danger',
      cancelText: 'No',
      onOk() {
        updateDeliveryStatus(logId, status);
      },
    });
  };

  const handleShowDeliveryOrder = (deliveryRecord) => {
  setSelectedDelivery(deliveryRecord);
  setShowDeliveryModal(true);
};

  // Get search input for table filtering
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
    render: text =>
      searchedColumn === dataIndex ? (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ) : (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
  });

  // Handle search in table
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  // Reset search filters
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  // Get delivery status color
  const getDeliveryStatusColor = (status) => {
    switch(status?.toUpperCase()) {
      case 'DELIVERED':
        return 'green';
      case 'PENDING':
        return 'orange';
      case 'CANCELLED':
        return 'red';
      default:
        return 'default';
    }
  };

  // Define table columns
  const columns = [
 {
  title: 'Order Date',
  dataIndex: 'timestamp',
  key: 'timestamp',
  sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
  defaultSortOrder: 'descend',
  render: (value) => (
    <Space>
      <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
      <span>{moment(value).format('DD-MMM-YY hh:mm')}</span>
    </Space>
  ),
},
    {
  title: 'Document No.',
  key: 'document_or_invoice',
  render: (_, record) => {
    const docNo = record.document_no || record.invoiceno; // prefer document_no, fallback to invoice_no
    return docNo ? (
      <Text copyable={{ text: docNo }}>
        {docNo}
      </Text>
    ) : (
      <Text type="secondary">-</Text>
    );
  },
},
    {
      title: 'Student',
      dataIndex: 'studentName',
      key: 'studentName',
      ...getColumnSearchProps('studentName'),
      sorter: (a, b) => a.studentName.localeCompare(b.studentName),
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
        </div>
      ),
    },

    {
      title: 'Purchase Origin',
      dataIndex: 'change_reason',
      key: 'change_reason',
      ...getColumnSearchProps('change_reason'),
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },

  {
    title: 'Item Name',
    dataIndex: 'item_name',
    key: 'item_name',
    ...getColumnSearchProps('inventory_name'),
    sorter: (a, b) => a.inventory_name.localeCompare(b.inventory_name),
    render: (text) => <Text strong>{text}</Text>,
  },
  {
    title: 'Category',
    dataIndex: 'category',
    key: 'category',
    filters: [...new Set(reportData.map(item => item.category))]
      .filter(Boolean)
      .map(cat => ({ text: cat, value: cat })),
    onFilter: (value, record) => record.category === value,
    render: (category) => <Tag color="blue">{category}</Tag>,
  },
  {
    title: 'Price per Unit',
    dataIndex: 'price_per_unit',
    key: 'price_per_unit',
    sorter: (a, b) => a.price_per_unit - b.price_per_unit,
    render: (price) => (
      <Text>฿{parseFloat(price).toFixed(2)}</Text>
    ),
  },
  {
    title: 'Payment Method',
    dataIndex: 'payment_method',
    key: 'payment_method',
    width: 140,
    render: (method) => {
      const color =
        method === 'unpaid'
          ? 'red'
          : method === 'cash'
          ? 'green'
          : method === 'bank_transfer'
          ? 'blue'
          : 'orange';
  
      const label =
        method === 'bank_transfer'
          ? 'Bank Transfer'
          : method === 'credit_card'
          ? 'Credit Card'
          : method === 'cash'
          ? 'Cash'
          : 'Unpaid';
  
      return <Tag color={color}>{label}</Tag>;
    },
  },
      {
      title: 'Quantity',
      dataIndex: 'quantity_change',
      key: 'quantity_change',
      sorter: (a, b) => Math.abs(a.quantity_change) - Math.abs(b.quantity_change),
      render: (quantity) => (
        <Text style={{ color: '#f5222d' }}>
          {Math.abs(quantity)} units
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'delivery_status',
      key: 'delivery_status',
      filters: [...new Set(reportData.map(item => item.delivery_status))]
        .filter(Boolean)
        .map(status => ({ text: status, value: status })),
      onFilter: (value, record) => record.delivery_status === value,
      render: (status) => (
        <Tag color={getDeliveryStatusColor(status)}>
          {status || 'PENDING'}
        </Tag>
      ),
    },
 
    {
      title: 'Delivery Date',
      dataIndex: 'deliver_date',
      key: 'deliver_date',
      sorter: (a, b) => new Date(a.deliver_date || 0) - new Date(b.deliver_date || 0),
      render: (text, record) => (
        record.deliver_date ? (
            <Space>
              <ClockCircleOutlined style={{ color: '#52c41a' }} />
              <span>{moment(record.deliver_date).format('DD-MMM-YY')}</span>
            </Space>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
  title: 'Delivered By',
  dataIndex: 'admin_nickname',
  key: 'admin_nickname',
  render: (text) => text ? (
    <Text strong>{text}</Text>
  ) : (
    <Text type="secondary">-</Text>
  ),
},
 {
  title: 'Actions',
  key: 'actions',
  width: 250,
  render: (text, record) => {
    const isPending = record.delivery_status === 'PENDING';
    const isDelivered = record.delivery_status === 'DELIVERED';
    const isCancelled = record.delivery_status === 'CANCELLED';
    const isUpdating = updating[record.log_id];
    const hasStudentName = record.studentName && record.studentName !== 'System Adjust' && record.studentName.trim() !== '';
    
    // If no valid student name, show no actions
    if (!hasStudentName) {
      return <Text type="secondary">System Entry</Text>;
    }
    
    if (isPending) {
         const isUnpaid = record.payment_method === 'unpaid';
   
         return (
           <Space>
             <Button
               type="primary"
               size="small"
               icon={<CheckCircleOutlined />}
               loading={isUpdating}
               disabled={isUnpaid}
               onClick={() => showConfirm(record.log_id, 'DELIVERED', record.studentName)}
             >
               {isUnpaid ? 'Unpaid' : 'Deliver'}
             </Button>
   
             <Button
               danger
               size="small"
               icon={<CloseCircleOutlined />}
               loading={isUpdating}
               onClick={() => showConfirm(record.log_id, 'CANCELLED', record.studentName)}
             >
               Cancel
             </Button>
           </Space>
         );
       }
    
    if (isDelivered) {
      return (
        <Space>
          <Button
            type="default"
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => handleShowDeliveryOrder(record)}
            title="Print Delivery Order"
          >
            Print DO
          </Button>
          <Button
            type="link"
            size="small"
            danger
            loading={isUpdating}
            onClick={() => showResetConfirm(record.log_id, record.studentName, record.delivery_status)}
          >
            Reset
          </Button>
        </Space>
      );
    }
    
    if (isCancelled) {
      return (
        <Button
          type="link"
          size="small"
          danger
          loading={isUpdating}
          onClick={() => showResetConfirm(record.log_id, record.studentName, record.delivery_status)}
        >
          Reset to Pending
        </Button>
      );
    }
    
    return <Text type="secondary">No actions available</Text>;
  },
}
  ];

  // Prepare CSV data
const csvData = filteredData.map(item => ({
  log_id: item.log_id,
  student_name: item.studentName,
  student_id: item.student_id,
  item_name: item.item_name,  // Add this line
  category: item.category,    // You might also want to add category
  price_per_unit: item.price_per_unit, // And price
  quantity: Math.abs(item.quantity_change),
  change_reason: item.change_reason,
  document_no: item.document_no || '',
  delivery_status: item.delivery_status,
  order_date: formatDateTime(item.timestamp),
  delivery_date: item.deliver_date ? formatDateTime(item.deliver_date) : '',
  delivered_by: item.admin_nickname || '' // And delivered by info
}));

  // Prepare CSV filename
  const getCSVFilename = () => {
    let filename = 'delivery_report';
    
    if (dateRange[0] && dateRange[1]) {
      filename += `_${dateRange[0].format('YYYYMMDD')}_to_${dateRange[1].format('YYYYMMDD')}`;
    } else {
      filename += `_${moment().format('YYYYMMDD')}`;
    }
    
    return `${filename}.csv`;
  };

  // Get summary statistics
  const getSummary = (pageData) => {
    const pendingCount = pageData.filter(item => item.delivery_status === 'PENDING').length;
    const deliveredCount = pageData.filter(item => item.delivery_status === 'DELIVERED').length;
    const cancelledCount = pageData.filter(item => item.delivery_status === 'CANCELLED').length;
      
    return (
      <>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={2}>
            <Text strong>Summary</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} colSpan={2}>
                        <Space>
              <Tag color="orange">Pending: {pendingCount}</Tag>
              <Tag color="green">Delivered: {deliveredCount}</Tag>
              <Tag color="red">Cancelled: {cancelledCount}</Tag>
            </Space>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} colSpan={2}>
            <Space>
              <Text strong></Text>
            </Space>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5}>

          </Table.Summary.Cell>
          <Table.Summary.Cell index={6}>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7}>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={8} colSpan={3}>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </>
    );
  };

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="delivery-report-container">
        <Card 
          title={<Title level={3}>Delivery Report</Title>}
          extra={
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={refreshData}
              loading={loading}
            >
              Refresh
            </Button>
          }
          className="delivery-card"
        >
          {error && (
            <div className="error-message">
              <Text type="danger">{error}</Text>
            </div>
          )}
          
          <Row gutter={16} className="filter-section">
            <Col xs={24} sm={24} md={20} lg={20} xl={20}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong><FilterOutlined /> Filters</Text>
                <Space wrap>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Text>Date Range:</Text>

                    {/* Start Date */}
                    <div>
                      <input
                        type="date"
                        id="hidden-range-start"
                        value={dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : ''}
                        onChange={(e) => {
                          const startDate = e.target.value ? dayjs(e.target.value, 'YYYY-MM-DD') : null;
                          const endDate = dateRange?.[1] || null;
                          handleDateRangeChange([startDate, endDate]);
                        }}
                        style={{ display: 'none' }}
                      />
                      <input
                        type="text"
                        readOnly
                        value={dateRange?.[0] ? dateRange[0].format('DD MMM YYYY') : ''}
                        placeholder="DD-MMM-YYYY"
                        onClick={() => document.getElementById('hidden-range-start')?.showPicker()}
                        style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          border: '1px solid #d9d9d9',
                          cursor: 'pointer',
                          width: '140px',
                          textAlign: 'center',
                        }}
                      />
                    </div>

                    <span>to</span>

                    {/* End Date */}
                    <div>
                      <input
                        type="date"
                        id="hidden-range-end"
                        value={dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : ''}
                        min={dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined}
                        onChange={(e) => {
                          const startDate = dateRange?.[0] || null;
                          const endDate = e.target.value ? dayjs(e.target.value, 'YYYY-MM-DD') : null;
                          handleDateRangeChange([startDate, endDate]);
                        }}
                        style={{ display: 'none' }}
                      />
                      <input
                        type="text"
                        readOnly
                        value={dateRange?.[1] ? dateRange[1].format('DD MMM YYYY') : ''}
                        placeholder="DD-MMM-YYYY"
                        onClick={() => document.getElementById('hidden-range-end')?.showPicker()}
                        style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          border: '1px solid #d9d9d9',
                          cursor: 'pointer',
                          width: '140px',
                          textAlign: 'center',
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Text>Status:</Text>
                    <Select
                      placeholder="All Statuses"
                      style={{ width: 150 }}
                      value={statusFilter || undefined}
                      onChange={(value) => {
                        setStatusFilter(value);
                        // Auto-apply filter when dropdown changes
                        let filtered = reportData;
                        
                        // Apply date range filter
                        if (dateRange[0] && dateRange[1]) {
                          const startDate = dateRange[0].startOf('day').valueOf();
                          const endDate = dateRange[1].endOf('day').valueOf();
                          
                          filtered = filtered.filter(item => {
                            const timestamp = new Date(item.timestamp).getTime();
                            return timestamp >= startDate && timestamp <= endDate;
                          });
                        }
                        
                        // Apply status filter
                        if (value) {
                          filtered = filtered.filter(item => item.delivery_status === value);
                        }
                        
                        setFilteredData(filtered);
                      }}
                      allowClear
                    >
                      <Option value="PENDING">Pending</Option>
                      <Option value="DELIVERED">Delivered</Option>
                      <Option value="CANCELLED">Cancelled</Option>
                    </Select>
                  </div>
                  <Button 
                    type="primary" 
                    onClick={applyFilters}
                    icon={<FilterOutlined />}
                  >
                    Apply
                  </Button>
                  <Button onClick={resetFilters}>
                    Reset
                  </Button>
                  <CSVLink
                    data={csvData}
                    filename={getCSVFilename()}
                    className="ant-btn ant-btn-primary export-button"
                    target="_blank"
                  >
                    <Button
                      type="primary"
                      icon={<FileExcelOutlined />}
                    >
                      Export CSV
                    </Button>
                  </CSVLink>
                </Space>
              </Space>
            </Col>
          </Row>
          
          <Divider />

          <Table
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            pagination={{
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
            }}
            summary={getSummary}
            size="middle"
            scroll={{ x: 'max-content' }}
            className="delivery-report-table"
          />
        </Card>
      </div>
      <DeliveryOrderModalB2
        show={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        deliveryData={selectedDelivery}
        />
    </div>
  );
};

export default withRoleAccess(DeliveryReport, ['superadmin', 'admin']);